import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';

function buildTreeData(index, knownIds) {
  const uniprotsByNode = new Map();
  const root = { name: '__root__', children: new Map() };
  for (const [uid, paths] of Object.entries(index)) {
    if (knownIds && !knownIds.has(uid)) continue;
    for (const pathStr of paths) {
      const parts = pathStr.split(/\s*->\s*/).map(s => s.trim()).filter(Boolean);
      if (!parts.length) continue;
      let keyParts = [];
      let node = root;
      for (const part of parts) {
        keyParts = [...keyParts, part];
        const key = keyParts.join('|||');
        if (!uniprotsByNode.has(key)) uniprotsByNode.set(key, new Set());
        uniprotsByNode.get(key).add(uid);
        if (!node.children.has(part)) {
          node.children.set(part, { name: part, children: new Map() });
        }
        node = node.children.get(part);
      }
    }
  }
  const toPlain = (node, kp) => {
    const ch = Array.from(node.children.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, c]) => toPlain(c, [...kp, c.name]));
    return { name: node.name, key: kp.join('|||'), children: ch.length ? ch : null };
  };
  const children = Array.from(root.children.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, c]) => toPlain(c, [c.name]));
  return { treeData: { name: 'MeSH', key: '', children }, uniprotsByNode };
}

export default function MeshClassificationFilter({ respiratoryOnly, onRespiratoryOnlyChange, onFilterChange, isDark, knownIds }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [meshIdx, setMeshIdx] = useState(null);
  const [respIdx, setRespIdx] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const selectedKeyRef = useRef(null);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${process.env.PUBLIC_URL}/data/mesh-index.json`).then(r => r.json()),
      fetch(`${process.env.PUBLIC_URL}/data/mesh-respiratory-index.json`).then(r => r.json()),
    ]).then(([mi, ri]) => {
      if (!cancelled) { setMeshIdx(mi); setRespIdx(ri); setLoading(false); }
    }).catch(err => {
      console.error('MeshClassificationFilter: failed to load MeSH data', err);
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const activeIndex = respiratoryOnly ? respIdx : meshIdx;

  const { treeData, uniprotsByNode } = useMemo(() => {
    if (!activeIndex) return { treeData: null, uniprotsByNode: new Map() };
    return buildTreeData(activeIndex, knownIds);
  }, [activeIndex, knownIds]);

  useEffect(() => {
    setSelectedKey(null);
    selectedKeyRef.current = null;
  }, [respiratoryOnly]);

  useEffect(() => {
    selectedKeyRef.current = selectedKey;
    onFilterChangeRef.current(selectedKey ? (uniprotsByNode.get(selectedKey) || new Set()) : null);
  }, [selectedKey, uniprotsByNode]);

  // D3 rendering — phylogenetic tree of life style
  useEffect(() => {
    if (!treeData || !svgRef.current || !containerRef.current) return;
    const container = containerRef.current;
    const svgEl = svgRef.current;
    const W = container.clientWidth || 800;

    const hier = d3.hierarchy(treeData);
    const numLeaves = hier.leaves().length;

    // Scale radius so leaves have enough spacing (~10px) to avoid label overlap
    const minRadius = W * 0.44;
    const leafRadius = Math.max(minRadius, (numLeaves * 30) / (2 * Math.PI));
    // Budget 180px for leaf labels (7.5px font × ~31 chars ≈ 140px + circle + offset)
    const labelMargin = 180;
    const svgSize = (leafRadius + labelMargin) * 2;

    d3.select(svgEl).selectAll('*').remove();

    const svg = d3.select(svgEl)
      .attr('width', W)
      .attr('height', W)   // square viewport; inner content is svgSize×svgSize
      .style('display', 'block');

    // Zoom group — centered in viewport
    const zoomG = svg.append('g');

    // d3.cluster puts all leaves at the same radius (like a cladogram)
    const treeLayout = d3.cluster()
      .size([2 * Math.PI, leafRadius])
      .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);

    treeLayout(hier);

    // Color by top-level category
    const colorOrd = d3.scaleOrdinal(d3.schemeTableau10);
    const getBranchColor = d => {
      let top = d;
      while (top.depth > 1 && top.parent) top = top.parent;
      if (top.depth === 0) return isDark ? '#555' : '#bbb';
      return colorOrd(top.data.name);
    };

    const cx = W / 2;
    const cy = W / 2;
    zoomG.attr('transform', `translate(${cx},${cy})`);

    // Zoom behaviour
    const zoom = d3.zoom()
      .scaleExtent([0.15, 8])
      .on('zoom', e => {
        zoomG.attr('transform',
          `translate(${cx + e.transform.x},${cy + e.transform.y}) scale(${e.transform.k})`);
      });
    svg.call(zoom);
    // Start at a zoom level that fits the full tree in the viewport
    const initialScale = W / svgSize;
    svg.call(zoom.transform, d3.zoomIdentity.scale(initialScale));

    // ── Links (Tree-of-Life style: arc at parent radius + radial line) ───
    const elbowLink = d => {
      const sa = d.source.x - Math.PI / 2;
      const ta = d.target.x - Math.PI / 2;
      const sr = d.source.y;
      const tr = d.target.y;
      // Start at source position
      const sx = sr * Math.cos(sa);
      const sy = sr * Math.sin(sa);
      // Intermediate point: arc endpoint at source radius, target angle
      const mx = sr * Math.cos(ta);
      const my = sr * Math.sin(ta);
      // End at target position
      const tx = tr * Math.cos(ta);
      const ty = tr * Math.sin(ta);
      // Arc sweep
      const da = ta - sa;
      const sweep = da > 0 ? 1 : 0;
      const largeArc = Math.abs(da) > Math.PI ? 1 : 0;
      return `M${sx},${sy}A${sr},${sr} 0 ${largeArc},${sweep} ${mx},${my}L${tx},${ty}`;
    };

    zoomG.append('g')
      .attr('fill', 'none')
      .selectAll('path')
      .data(hier.links())
      .join('path')
      .attr('stroke', d => {
        const col = d3.color(getBranchColor(d.target));
        if (col) col.opacity = isDark ? 0.35 : 0.3;
        return col ? col.toString() : (isDark ? '#444' : '#ccc');
      })
      .attr('stroke-width', d => d.source.depth === 0 ? 1.2 : 0.7)
      .attr('d', elbowLink);

    // ── Tooltip ────────────────────────────────────────────────────────────
    const tip = d3.select(container).append('div')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', isDark ? 'rgba(20,20,20,0.94)' : 'rgba(255,255,255,0.97)')
      .style('border', `1px solid ${isDark ? '#555' : '#ccc'}`)
      .style('border-radius', '7px')
      .style('padding', '7px 10px')
      .style('font-size', '12px')
      .style('color', isDark ? '#eee' : '#222')
      .style('opacity', '0')
      .style('z-index', '200')
      .style('max-width', '260px')
      .style('word-break', 'break-word')
      .style('line-height', '1.5');

    // ── Nodes ──────────────────────────────────────────────────────────────
    const nodeG = zoomG.append('g');

    const nodeEnter = nodeG.selectAll('g')
      .data(hier.descendants())
      .join('g')
      .attr('transform', d => {
        const angle = d.x - Math.PI / 2;
        return `translate(${d.y * Math.cos(angle)},${d.y * Math.sin(angle)})`;
      })
      .style('cursor', d => d.data.key ? 'pointer' : 'default');

    // Circles — only on internal (branching) nodes, not leaves
    // nodeEnter.filter(d => d.depth > 0 && d.children)
    //   .append('circle')
    //   .attr('r', d => {
    //     const cnt = uniprotsByNode.get(d.data.key)?.size ?? 0;
    //     return Math.max(2.5, Math.min(7, 2 + cnt / 90));
    //   })
    //   .attr('fill', d => getBranchColor(d))
    //   .attr('stroke', isDark ? '#111' : '#fff')
    //   .attr('stroke-width', 0.8);
    //   .attr('class', 'cls-node-circle');

    // Leaf labels (text at the tip, rotated radially)
    nodeEnter.filter(d => !d.children && d.depth > 0)
      .append('text')
      .attr('dy', '0.31em')
      .attr('x', d => d.x < Math.PI ? 5 : -5)
      .attr('text-anchor', d => d.x < Math.PI ? 'start' : 'end')
      .attr('transform', d => {
        const deg = d.x * 180 / Math.PI - 90;
        return `rotate(${d.x >= Math.PI ? deg + 180 : deg})`;
      })
      .style('font-size', '7.5px')
      .style('fill', isDark ? '#b0b0b0' : '#444')
      .style('user-select', 'none')
      .style('cursor', 'pointer')
      .text(d => {
        const name = d.data.name;
        return name.length > 32 ? name.slice(0, 31) + '…' : name;
      });

    // Interaction: hover & click
    nodeEnter
      .on('mouseover', (event, d) => {
        if (!d.data.key) return;
        const cnt = uniprotsByNode.get(d.data.key)?.size ?? 0;
        tip
          .html(`<strong>${d.data.name}</strong><br/>${cnt} protein${cnt !== 1 ? 's' : ''}`)
          .style('opacity', '1');
      })
      .on('mousemove', event => {
        const rect = container.getBoundingClientRect();
        tip
          .style('left', `${Math.min(event.clientX - rect.left + 14, W - 270)}px`)
          .style('top', `${Math.max(event.clientY - rect.top - 46, 4)}px`);
      })
      .on('mouseout', () => tip.style('opacity', '0'))
      .on('click', (e, d) => {
        if (!d.data.key) return;
        e.stopPropagation();
        const newKey = selectedKeyRef.current === d.data.key ? null : d.data.key;
        selectedKeyRef.current = newKey;
        setSelectedKey(newKey);
        // Highlight selected node
        nodeG.selectAll('.cls-node-circle')
          .attr('stroke', nd => nd.data.key === newKey ? (isDark ? '#fff' : '#222') : (isDark ? '#111' : '#fff'))
          .attr('stroke-width', nd => nd.data.key === newKey ? 2.5 : 0.8);
      });

    // Click on background → clear selection
    svg.on('click', () => {
      if (!selectedKeyRef.current) return;
      selectedKeyRef.current = null;
      setSelectedKey(null);
      nodeG.selectAll('.cls-node-circle')
        .attr('stroke', isDark ? '#111' : '#fff')
        .attr('stroke-width', 0.8);
    });

    return () => { tip.remove(); };
  }, [treeData, isDark, uniprotsByNode]); // eslint-disable-line

  const selectedCount = selectedKey ? (uniprotsByNode.get(selectedKey)?.size ?? 0) : null;

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 12, opacity: 0.7, fontSize: '0.9rem' }}>
      Loading MeSH data…
    </div>
  );
  if (!activeIndex) return (
    <div style={{ textAlign: 'center', padding: 12, opacity: 0.7, fontSize: '0.9rem' }}>
      MeSH data unavailable.
    </div>
  );

  return (
    <div ref={containerRef} className="mesh-d3-container" style={{ position: 'relative' }}>
      <div style={{ padding: '6px 8px 8px 8px', borderBottom: '1px solid rgba(128,128,128,0.25)' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={respiratoryOnly}
            onChange={e => onRespiratoryOnlyChange && onRespiratoryOnlyChange(e.target.checked)}
          />
          Show filters for respiratory related conditions only
        </label>
      </div>
      <svg ref={svgRef} style={{ display: 'block', width: '100%' }} />
      <div style={{ borderTop: '1px solid rgba(128,128,128,0.25)' }} className="mesh-d3-infobar">
        {selectedKey
          ? <span className="mesh-d3-selected-name">
              ◉ {selectedKey.split('|||').pop()} — {selectedCount} protein{selectedCount !== 1 ? 's' : ''}
            </span>
          : <span className="mesh-d3-hint">
              Click on a medical condition to filter · Scroll to zoom · Drag to pan
            </span>
        }
        {selectedKey && (
          <button
            className="mesh-tree-reset"
            title="Clear filter"
            onClick={() => { setSelectedKey(null); selectedKeyRef.current = null; }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: 'middle' }}>
              autorenew
            </span>
            <span style={{ fontSize: 13, verticalAlign: 'middle', marginLeft: 4 }}>Clear selection</span>
          </button>
        )}
      </div>
    </div>
  );
}
